import React, { useState, useMemo, useEffect } from 'react';
import { BarChart3, DollarSign, Package, ShoppingCart, TrendingUp, ArrowRight, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { OrderDetailsModal } from '../../components/admin/OrderDetailsModal';
import { Booking } from '../../types';
import { supabase } from '../../utils/supabase';

export function DashboardHome() {
  const [selectedOrder, setSelectedOrder] = useState<Booking | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dashboardData, setDashboardData] = useState({
    net_revenue: 0,
    completed_orders: 0,
    total_revenue: 0,
    total_refunds: 0,
    this_month_orders: 0,
    last_month_orders: 0,
    monthly_growth: 0,
    refund_count: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [orders, setOrders] = useState<Booking[]>([]);

  // Fetch dashboard totals from server function
  const fetchDashboardTotals = async () => {
    try {
      console.log('Fetching dashboard totals...');
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/dashboard-totals`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Dashboard totals request failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('Dashboard totals received:', result);

      if (result.success && result.data) {
        setDashboardData(result.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard totals:', error);
      // Fallback to local calculation if server function fails
      await fetchOrdersAndCalculate();
    }
  };

  // Fallback: Load orders and calculate locally
  const fetchOrdersAndCalculate = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setOrders(data || []);
      
      // Calculate metrics locally as fallback
      const completedOrders = (data || []).filter(order => order.status === 'Done');
      const refundedOrders = (data || []).filter(order => order.status === 'Return/Refund');
      
      const totalRevenue = completedOrders.reduce((sum, order) => {
        const price = typeof order.product.price === 'string' 
          ? parseFloat(order.product.price) 
          : order.product.price;
        return sum + (isNaN(price) ? 0 : price);
      }, 0);

      const totalRefunds = refundedOrders.reduce((sum, order) => {
        const refundAmount = order.refund?.amount 
          ? (typeof order.refund.amount === 'string' 
              ? parseFloat(order.refund.amount) 
              : order.refund.amount)
          : (typeof order.product.price === 'string'
              ? parseFloat(order.product.price)
              : order.product.price);
        return sum + (isNaN(refundAmount) ? 0 : refundAmount);
      }, 0);

      setDashboardData({
        net_revenue: totalRevenue - totalRefunds,
        completed_orders: completedOrders.length,
        total_revenue: totalRevenue,
        total_refunds: totalRefunds,
        this_month_orders: 0, // Simplified for fallback
        last_month_orders: 0,
        monthly_growth: 0,
        refund_count: refundedOrders.length
      });
    } catch (error) {
      console.error('Error in fallback calculation:', error);
    }
  };

  // Set up real-time subscription for order changes
  useEffect(() => {
    // Initial fetch
    fetchDashboardTotals();

    // Set up real-time subscription for order status changes
    const subscription = supabase
      .channel('orders_dashboard_sync')
      .on(
        'postgres_changes',
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'orders'
        },
        (payload) => {
          console.log('Order status changed, refreshing dashboard:', payload);
          // Refresh dashboard when any order status changes
          fetchDashboardTotals();
        }
      )
      .on(
        'postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'orders'
        },
        (payload) => {
          console.log('New order created, refreshing dashboard:', payload);
          fetchDashboardTotals();
        }
      )
      .on(
        'postgres_changes',
        { 
          event: 'DELETE', 
          schema: 'public', 
          table: 'orders'
        },
        (payload) => {
          console.log('Order deleted, refreshing dashboard:', payload);
          fetchDashboardTotals();
        }
      )
      .subscribe();

    // Also set up periodic refresh every 30 seconds as backup
    const interval = setInterval(() => {
      fetchDashboardTotals();
    }, 30000);

    setIsLoading(false);

    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, []);

  // Manual refresh handler
  const handleManualRefresh = () => {
    setRefreshKey(prev => prev + 1);
    fetchDashboardTotals();
  };

  // Get completed orders for display
  const completedOrders = useMemo(() => {
    return [...orders]
      .filter(order => order.status === 'Done')
      .sort((a, b) => {
        const dateA = new Date(a.status_history[a.status_history.length - 1].timestamp);
        const dateB = new Date(b.status_history[b.status_history.length - 1].timestamp);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 5);
  }, [orders]);

  // Calculate average order value
  const averageOrderValue = dashboardData.completed_orders > 0 
    ? dashboardData.net_revenue / dashboardData.completed_orders 
    : 0;

  // Calculate conversion rate (assuming we have total orders)
  const totalOrders = orders.length;
  const conversionRate = totalOrders > 0 
    ? (dashboardData.completed_orders / totalOrders) * 100 
    : 0;

  const stats = [
    {
      name: 'Net Revenue',
      value: `$${dashboardData.net_revenue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`,
      change: `${dashboardData.monthly_growth >= 0 ? '+' : ''}${dashboardData.monthly_growth.toFixed(1)}%`,
      icon: DollarSign,
      trend: dashboardData.monthly_growth >= 0 ? 'up' : 'down',
    },
    {
      name: 'Completed Sales',
      value: dashboardData.completed_orders.toString(),
      change: `${dashboardData.this_month_orders - dashboardData.last_month_orders >= 0 ? '+' : ''}${dashboardData.this_month_orders - dashboardData.last_month_orders}`,
      icon: ShoppingCart,
      trend: dashboardData.this_month_orders - dashboardData.last_month_orders >= 0 ? 'up' : 'down',
    },
    {
      name: 'This Month',
      value: dashboardData.this_month_orders.toString(),
      change: `${dashboardData.monthly_growth >= 0 ? '+' : ''}${dashboardData.monthly_growth.toFixed(1)}%`,
      icon: TrendingUp,
      trend: dashboardData.monthly_growth >= 0 ? 'up' : 'down',
    },
    {
      name: 'Avg Order Value',
      value: `$${averageOrderValue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`,
      change: `${conversionRate.toFixed(1)}% conversion`,
      icon: BarChart3,
      trend: 'up',
    },
  ];

  // Generate sample chart data (you can enhance this with real monthly data)
  const chartData = [
    { name: 'Jan', sales: dashboardData.net_revenue * 0.8, refunds: dashboardData.total_refunds * 0.6 },
    { name: 'Feb', sales: dashboardData.net_revenue * 0.9, refunds: dashboardData.total_refunds * 0.7 },
    { name: 'Mar', sales: dashboardData.net_revenue * 1.1, refunds: dashboardData.total_refunds * 0.8 },
    { name: 'Apr', sales: dashboardData.net_revenue * 0.95, refunds: dashboardData.total_refunds * 0.9 },
    { name: 'May', sales: dashboardData.net_revenue * 1.05, refunds: dashboardData.total_refunds * 1.0 },
    { name: 'Jun', sales: dashboardData.net_revenue, refunds: dashboardData.total_refunds },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-full ${
                stat.trend === 'up' ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <stat.icon className={`w-6 h-6 ${
                  stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                }`} />
              </div>
            </div>
            <div className="mt-2">
              <span className={`text-sm font-medium ${
                stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {stat.change}
              </span>
              <span className="text-sm text-gray-600"> from last month</span>
            </div>
          </div>
        ))}
      </div>

      {/* Sales Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Sales Overview</h2>
            <button
              onClick={handleManualRefresh}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              title="Refresh data"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `$${Math.abs(value).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}`,
                    name === 'sales' ? 'Net Sales' : 'Refunds'
                  ]}
                />
                <Bar dataKey="sales" fill="#3B82F6" name="Net Sales" />
                <Bar dataKey="refunds" fill="#EF4444" name="Refunds" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Refund Tracker */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-gray-900">Refund Tracker</h2>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Total Refunds</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData.refund_count}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Refund Amount</p>
              <p className="text-2xl font-semibold text-gray-900">
                ${dashboardData.total_refunds.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Recent Activity</h3>
            <div className="space-y-4">
              <div className="text-center py-6 text-gray-500">
                <p>Real-time sync active</p>
                <p className="text-xs mt-1">Dashboard updates automatically when orders change status</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Completed Orders */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Completed Orders</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {completedOrders.length > 0 ? (
            completedOrders.map((order) => (
              <div
                key={order.id}
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedOrder(order)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Order #{order.order_number}</p>
                    <p className="text-sm text-gray-600">{order.customer_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      ${typeof order.product.price === 'string'
                        ? parseFloat(order.product.price).toFixed(2)
                        : order.product.price.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(order.status_history[order.status_history.length - 1].timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {order.status}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedOrder(order);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                  >
                    View Details
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center text-gray-500">
              <p>No completed orders yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onSaved={() => fetchDashboardTotals()}
          order={selectedOrder}
        />
      )}
    </div>
  );
}